import logger from './logger';
import LanguageCode, { Translations } from '../types/language';
import { toTranslatorLanguageCode } from './translatorLanguageCode';

type TranslateCardReaderTextDeps = {
	getString: (key?: string) => string;
	currentLanguage: LanguageCode;
	autoTranslate: (translations: Translations, toLang: string) => Promise<Translations | undefined>;
	setTranslation: (language: LanguageCode, key: string, value: string) => void;
};

/**
 * Longest a card screen waits on a live translation before showing the reader text as-is.
 * The API client has no request timeout, and a failed payment is when the network is most
 * likely to be stalling too.
 */
export const CARD_READER_TRANSLATION_TIMEOUT_MS = 3000;

/**
 * Looks up a card-reader sentence, including error text such as
 * "Timed out waiting for card." Unknown sentences are translated once and cached.
 */
export async function translateCardReaderText(msg: string, deps: TranslateCardReaderTextDeps): Promise<string> {
	const translated = deps.getString(msg);
	if (translated !== msg || deps.currentLanguage === 'en') return translated;

	const language = deps.currentLanguage;
	const request = deps.autoTranslate({ [msg]: msg }, toTranslatorLanguageCode(language));
	// Keeps running past the time limit, so a late answer is still cached for the next failure.
	const translation = request
		.then((result) => {
			const value = result?.[msg];
			if (value) deps.setTranslation(language, msg, value);
			return value;
		})
		.catch((err) => {
			logger.logGeneral.error({}, 'Failed to auto-translate device message', err);
			return undefined;
		});

	let timer: ReturnType<typeof setTimeout> | undefined;
	const timeLimit = new Promise<undefined>((resolve) => {
		timer = setTimeout(() => resolve(undefined), CARD_READER_TRANSLATION_TIMEOUT_MS);
	});

	const value = await Promise.race([translation, timeLimit]);
	clearTimeout(timer);
	return value || msg;
}
